import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { avatarSrc, useBdQuery } from "../hooks/useBdApi";
import { KpiStrip } from "../components/Kpi";
import { HorizontalBars, BreakdownDonut } from "../components/Charts";
import TrendCard from "../components/TrendCard";
import DataTable from "../components/DataTable";
import { AgpPaceStrip } from "../agp/components/AgpPaceStrip";
import { Avatar, Card, EmptyState, ErrorState, Pill, ProgressBar, Skeleton, Tabs, TeamDot } from "../components/Ui";
import SourceTip, { TitleWithSource } from "../components/SourceTip";
import { CallReportsTable, DealsTable } from "../components/ReportTables";
import {
  CLIENT_STATUS_TONE,
  PRIMARY_KPIS,
  clientSegmentsSub,
  clientsSub,
  formatCurrency,
  formatDate,
  formatDecimal,
  formatNumber,
  healthTone,
  moneyOrDash,
  teamColor,
  teamShortName,
} from "../metrics";

const COMPARE_METRICS = [
  { key: "pointValue", label: "Point value", format: (v) => formatDecimal(v) },
  { key: "meetingsBooked", label: "Meetings booked", format: (v) => formatNumber(v) },
  { key: "meetingsHappened", label: "Meetings held", format: (v) => formatNumber(v) },
  { key: "tapsHeld", label: "TAPs held", format: (v) => formatNumber(v) },
  { key: "lmvHeld", label: "LMV meetings held", format: (v) => formatNumber(v) },
  { key: "eventsHeld", label: "Events held", format: (v) => formatNumber(v) },
  { key: "bdDealsClosed", label: "BD deals closed", format: (v) => formatNumber(v) },
  { key: "newClients", label: "New clients", format: (v) => formatNumber(v) },
  { key: "curateDinners", label: "Signed dinners", format: (v) => formatNumber(v) },
  { key: "liveClients", label: "Clients", format: (v) => formatNumber(v) },
  { key: "retainedClients", label: "Retainer clients", format: (v) => formatNumber(v) },
  { key: "pplClients", label: "PPL clients", format: (v) => formatNumber(v) },
  { key: "commissionClients", label: "Commission-only clients", format: (v) => formatNumber(v) },
];

export default function TeamPage({ period, meta }) {
  const { teamKey } = useParams();
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useBdQuery(`teams/${teamKey}`, period.apiParams);
  const [tab, setTab] = useState("members");
  const [compareMetric, setCompareMetric] = useState("pointValue");
  const [subTeamFilter, setSubTeamFilter] = useState("all");
  const [showInactive, setShowInactive] = useState(false);
  const [showTerminated, setShowTerminated] = useState(false);

  const members = useMemo(() => {
    if (!data) return [];
    return data.members.filter((m) => showInactive || m.isActive || m.points || m.meetingsBooked || m.meetingsHappened || m.revenue);
  }, [data, showInactive]);

  const clients = useMemo(() => {
    if (!data) return [];
    return data.clients
      .filter((c) => showTerminated || c.isBdActive)
      .filter((c) => subTeamFilter === "all" || (subTeamFilter === "core" ? !c.subTeam : c.subTeam === subTeamFilter));
  }, [data, subTeamFilter, showTerminated]);

  if (error) return <ErrorState message={error} onRetry={refetch} />;

  const team = data ? data.team : null;
  const kpis = data ? data.kpis : null;
  const comparison = data ? data.comparison : null;
  const color = teamColor(team ? team.name : "");
  const metric = COMPARE_METRICS.find((m) => m.key === compareMetric) || COMPARE_METRICS[0];
  const deals = data ? data.deals || [] : [];
  const callReports = data ? data.callReports || [] : [];

  return (
    <div className="bd-page" style={{ "--team-color": color }}>
      <div className="bd-page__head">
        <div>
          <div className="bd-eyebrow">
            <Link to={`/landmark/bd${period.search}`} className="bd-link">
              ← All teams
            </Link>
          </div>
          <h1 className="bd-page__title-with-dot">
            {team ? (
              <>
                <TeamDot team={team.name} /> {team.name}
              </>
            ) : (
              <Skeleton width={260} height={28} />
            )}
          </h1>
          <p className="bd-page__lede">
            {data ? (
              <>
                {team.memberCount} active members · {formatNumber(kpis.liveClients)} clients <SourceTip metric="liveClients" /> ({formatNumber(kpis.retainedClients)} retainer ·{" "}
                {formatNumber(kpis.pplClients)} PPL · {formatNumber(kpis.commissionClients)} commission only)
                {data.subTeams.length > 0 && <> · sub-teams: {data.subTeams.join(", ")}</>}
              </>
            ) : (
              <Skeleton width={200} height={14} />
            )}
          </p>
        </div>
        <div className="bd-page__head-meta">
          {kpis && (
            <>
              <Pill tone="neutral">
                {formatNumber(kpis.meetingsUpcoming)} upcoming meetings <SourceTip metric="meetingsUpcoming" />
              </Pill>
              <Pill tone="neutral">
                {formatNumber(kpis.pipelineCount)} open opps <SourceTip metric="pipeline" />
              </Pill>
            </>
          )}
        </div>
      </div>

      <KpiStrip
        kpis={kpis}
        comparison={comparison}
        definitions={PRIMARY_KPIS}
        loading={loading && !data}
        subs={{
          pointValue: kpis ? `${formatNumber(kpis.points)} points` : null,
          bluesBooked: kpis ? `${formatNumber(kpis.clientMeetings)} client · ${formatNumber(kpis.ppClientMeetings)} PP` : null,
          tapsBooked: kpis ? `${formatNumber(kpis.tapsHeld)} TAPs held` : null,
          meetingsHappened: kpis ? `${formatNumber(kpis.tapsHeld)} TAPs · ${formatNumber(kpis.callsHeld)} calls` : null,
          lmvHeld: kpis ? `${formatNumber(kpis.lmv)} LMVs booked` : null,
          bdDealsClosed: kpis && kpis.bdDealsAmount ? moneyOrDash(kpis.bdDealsAmount) : null,
          liveClients: kpis ? `${clientSegmentsSub(kpis)} · ${clientsSub(data.period)}` : null,
        }}
      />

      <AgpPaceStrip teamKey={teamKey} />

      <div className="bd-grid bd-grid--2-1">
        <TrendCard
          period={period}
          pageTrend={data ? data.trend : []}
          pageGranularity={data ? data.period.granularity : undefined}
          loading={loading && !data}
          scope="team"
          team={teamKey}
          subtitle={team ? team.name : ""}
          dataWindow={meta ? meta.dataWindow : null}
        />
        <Card
          title={<TitleWithSource metric={metric.key}>Versus other teams</TitleWithSource>}
          actions={
            <select className="bd-select" value={compareMetric} onChange={(e) => setCompareMetric(e.target.value)}>
              {COMPARE_METRICS.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
          }
        >
          {loading && !data ? (
            <Skeleton height={200} />
          ) : (
            <HorizontalBars
              rows={data.teamComparison.map((t) => ({
                key: t.key,
                name: teamShortName(t.name),
                value: t[metric.key],
                color: teamColor(t.name),
                highlight: t.key === teamKey,
              }))}
              format={metric.format}
              onClick={(r) => r.key !== teamKey && navigate(`/landmark/bd/team/${r.key}${period.search}`)}
            />
          )}
          <div className="bd-card__divider" />
          <h4 className="bd-card__minor-title">
            <TitleWithSource metric="pointsByType">Points by type</TitleWithSource>
          </h4>
          {loading && !data ? <Skeleton height={160} /> : <BreakdownDonut byType={kpis.byType} height={170} />}
        </Card>
      </div>

      <Card
        flush
        title={
          <Tabs
            tabs={[
              { id: "members", label: "Members", count: data ? members.length : undefined },
              { id: "clients", label: "Clients", count: data ? clients.length : undefined },
              { id: "deals", label: "Closed won", count: data ? deals.length : undefined },
              { id: "calls", label: "Call reports", count: data ? callReports.length : undefined },
            ]}
            active={tab}
            onChange={setTab}
          />
        }
        actions={
          <>
            {tab === "members" && (
              <label className="bd-checkbox">
                <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
                <span>Show inactive</span>
              </label>
            )}
            {tab === "clients" && (
              <>
                <label className="bd-checkbox">
                  <input type="checkbox" checked={showTerminated} onChange={(e) => setShowTerminated(e.target.checked)} />
                  <span>Show terminated</span>
                </label>
                {data && data.subTeams.length > 0 && (
                  <div className="bd-chip-row">
                    {[
                      { id: "all", label: "All" },
                      ...data.subTeams.map((s) => ({ id: s, label: s })),
                      { id: "core", label: "No sub-team" },
                    ].map((s) => (
                      <button key={s.id} type="button" className={`bd-chip ${subTeamFilter === s.id ? "is-active" : ""}`} onClick={() => setSubTeamFilter(s.id)}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
                <SourceTip metric="clientsTable" title="Clients" />
              </>
            )}
            {tab === "deals" && <SourceTip metric="dealsWon" title="Closed won" />}
            {tab === "calls" && <SourceTip metric="callReports" title="Call reports" />}
          </>
        }
      >
        {tab === "members" ? (
          <DataTable
            loading={loading && !data}
            rows={members}
            rowKey={(r) => r.sfUserId}
            onRowClick={(r) => navigate(`/landmark/bd/member/${r.sfUserId}${period.search}`)}
            defaultSort={{ key: "pointValue", dir: "desc" }}
            emptyTitle="No members with activity"
            emptyMessage="Try a wider period or show inactive members."
            columns={[
              {
                key: "name",
                label: "Member",
                sortValue: (r) => r.name,
                render: (r) => (
                  <span className="bd-person">
                    <Avatar name={r.name} team={r.team} size="sm" src={avatarSrc(r)} />
                    <span className="bd-person__text">
                      <span>
                        {r.name}
                        {!r.isActive && (
                          <Pill tone="neutral" className="bd-ml-1">
                            inactive
                          </Pill>
                        )}
                      </span>
                      <small>{[r.title, r.roleName].filter(Boolean).join(" · ") || "—"}</small>
                    </span>
                  </span>
                ),
              },
              { key: "pointValue", label: "Pts", metric: "pointValue", align: "right", render: (r) => <strong>{formatDecimal(r.pointValue)}</strong> },
              { key: "taps", label: "TAPs", metric: "taps", align: "right", hideBelow: "md", render: (r) => formatNumber(r.taps) },
              { key: "clientMeetings", label: "Client mtgs", metric: "clientMeetings", align: "right", hideBelow: "md", render: (r) => formatNumber(r.clientMeetings) },
              { key: "curateAttendees", label: "Curate", metric: "curateAttendees", align: "right", hideBelow: "lg", render: (r) => formatNumber(r.curateAttendees) },
              { key: "meetingsBooked", label: "Booked", metric: "meetingsBooked", align: "right", render: (r) => formatNumber(r.meetingsBooked) },
              { key: "meetingsHappened", label: "Held", metric: "meetingsHappened", align: "right", render: (r) => formatNumber(r.meetingsHappened) },
              { key: "tapsHeld", label: "TAPs held", metric: "tapsHeld", align: "right", hideBelow: "lg", render: (r) => formatNumber(r.tapsHeld) },
              { key: "newClients", label: "New clients", metric: "newClients", align: "right", hideBelow: "lg", render: (r) => formatNumber(r.newClients) },
              { key: "bdDealsClosed", label: "BD deals", metric: "bdDealsClosed", align: "right", hideBelow: "lg", render: (r) => formatNumber(r.bdDealsClosed) },
              { key: "revenue", label: "Revenue", metric: "revenue", align: "right", render: (r) => <span className={r.revenue ? "" : "bd-muted"}>{moneyOrDash(r.revenue)}</span> },
            ]}
          />
        ) : tab === "deals" ? (
          <DealsTable rows={deals} loading={loading && !data} search={period.search} showOwner />
        ) : tab === "calls" ? (
          <CallReportsTable rows={callReports} loading={loading && !data} search={period.search} showOwner />
        ) : (
          <DataTable
            loading={loading && !data}
            rows={clients}
            rowKey={(r) => r.sfId}
            defaultSort={{ key: "meetingsInPeriod", dir: "desc" }}
            pageSize={25}
            emptyTitle="No BD clients"
            emptyMessage={showTerminated ? undefined : "Tick 'Show terminated' to include clients with a Terminated Date."}
            columns={[
              {
                key: "name",
                label: "Client",
                metric: "clientsTable",
                sortValue: (r) => r.name,
                render: (r) => (
                  <span className="bd-person__text">
                    <span>
                      {r.name}
                      {r.subTeam && (
                        <Pill tone="info" className="bd-ml-1">
                          {r.subTeam}
                        </Pill>
                      )}
                      {!r.isBdActive && (
                        <Pill tone="danger" className="bd-ml-1">
                          terminated {formatDate(r.terminatedDate, { year: true })}
                        </Pill>
                      )}
                    </span>
                    <small>{r.accountManager && r.accountManager.name ? `AM: ${r.accountManager.name}` : "—"}</small>
                  </span>
                ),
              },
              {
                key: "status",
                label: "Status",
                width: 120,
                render: (r) => <Pill tone={CLIENT_STATUS_TONE[r.status] || "neutral"}>{r.status || "—"}</Pill>,
              },
              { key: "contractType", label: "Contract", hideBelow: "lg", render: (r) => r.contractType || "—" },
              { key: "sfCreatedDate", label: "Created", metric: "bdClientsNew", hideBelow: "lg", render: (r) => formatDate(r.sfCreatedDate), sortValue: (r) => r.sfCreatedDate },
              { key: "meetingsInPeriod", label: "Mtgs (period)", metric: "meetingsTable", align: "right", render: (r) => formatNumber(r.meetingsInPeriod) },
              { key: "oppsPerMonth", label: "Opps / mo", align: "right", hideBelow: "lg", render: (r) => (r.oppsPerMonth ? formatDecimal(r.oppsPerMonth) : <span className="bd-muted">—</span>) },
              {
                key: "occurredThisMonth",
                label: "This month vs target",
                width: 180,
                hideBelow: "md",
                sortValue: (r) => (r.targetPerMonth ? (r.occurredThisMonth || 0) / r.targetPerMonth : -1),
                render: (r) =>
                  r.targetPerMonth ? (
                    <span className="bd-target">
                      <ProgressBar value={r.occurredThisMonth || 0} max={r.targetPerMonth} tone={(r.occurredThisMonth || 0) >= r.targetPerMonth ? "success" : "info"} />
                      <small>
                        {formatNumber(r.occurredThisMonth)} held · {formatNumber(r.scheduledThisMonth)} sched / {formatNumber(r.targetPerMonth)}
                      </small>
                    </span>
                  ) : (
                    <span className="bd-muted">no target</span>
                  ),
              },
              {
                key: "healthScore",
                label: "Health",
                align: "right",
                width: 90,
                render: (r) =>
                  r.healthScore === null || r.healthScore === undefined ? (
                    <span className="bd-muted">—</span>
                  ) : (
                    <Pill tone={healthTone(r.healthScore)}>{formatNumber(r.healthScore)}</Pill>
                  ),
              },
              {
                key: "retainerFee",
                label: "Retainer",
                align: "right",
                hideBelow: "lg",
                render: (r) => (r.retainerFee ? formatCurrency(r.retainerFee) : <span className="bd-muted">—</span>),
              },
              {
                key: "commissionFees",
                label: "Commission fees",
                align: "right",
                hideBelow: "lg",
                render: (r) => (r.commissionFees ? formatCurrency(r.commissionFees) : <span className="bd-muted">—</span>),
              },
              { key: "termEndDate", label: "Term ends", hideBelow: "lg", render: (r) => formatDate(r.termEndDate), sortValue: (r) => r.termEndDate },
            ]}
          />
        )}
      </Card>

      <Card
        flush
        title={<TitleWithSource metric="callReports">Recent call reports</TitleWithSource>}
        subtitle={`Latest TAP / call report Tasks for ${team ? team.name : "this team"} in the period (meetings held)`}
      >
        <CallReportsTable rows={callReports} loading={loading && !data} search={period.search} showOwner />
      </Card>

      {data && !members.length && !clients.length && <EmptyState title="No data for this team" />}
    </div>
  );
}
